import { formatDate, secondsSinceMidnightToTimeString } from '@/helpers/dateHelper';
import { strings } from '@/services/translation/strings';
import type { ScheduleChangeRequest } from '@/services/workingPatterns/useWorkingPatternRequests';
import { ReactNode } from 'react';

interface GroupedScheduleChangeRequestDetailsCardProps {
  requests: ScheduleChangeRequest[];
  actions?: ReactNode;
}

const formatTime = (value?: number) => (value == null ? 'Not scheduled' : secondsSinceMidnightToTimeString(value));

export const MyScheduleGroupedScheduleChangeRequestDetailCard = ({
  requests,
  actions
}: GroupedScheduleChangeRequestDetailsCardProps) => {
  const grouped = requests
    .slice()
    .sort((a, b) => (a.effectiveFrom ?? '').localeCompare(b.effectiveFrom ?? ''))
    .reduce(
      (acc, request) => {
        const date = request.effectiveFrom ?? 'Unknown';

        if (!acc[date]) {
          acc[date] = [];
        }

        acc[date].push(request);

        return acc;
      },
      {} as Record<string, ScheduleChangeRequest[]>
    );

  return (
    <div className="space-y-8">
      {Object.entries(grouped).map(([date, dateRequests]) => (
        <div key={date} className="space-y-6">
          <h3 className="border-b pb-2 text-lg font-semibold">
            {strings.timeshift.teamMember.requests.scheduleUpdate.effectiveFrom}: {formatDate(date)}
          </h3>

          {dateRequests.map(request => (
            <div key={request.id} className="space-y-2">
              {request.requestedOn && (
                <p className="text-gray-400">
                  {strings.timeshift.teamMember.requests.scheduleUpdate.requestSubmitted(
                    formatDate(request.requestedOn)
                  )}
                </p>
              )}

              <div className="space-y-1">
                {request.dayChanges?.map(change => (
                  <div
                    key={`${request.id}-${change.dayNumber}-${change.slotNumber}`}
                    className="flex flex-row space-x-32"
                  >
                    <div className="w-64">
                      <p>{strings.timeshift.teamMember.requests.scheduleUpdate.hoursBefore(change.day)}</p>

                      <p>
                        {formatTime(change.before?.startTime)} - {formatTime(change.before?.endTime)}
                      </p>
                    </div>

                    <div className="w-64">
                      <p>{strings.timeshift.teamMember.requests.scheduleUpdate.hoursAfter(change.day)}</p>

                      <p>
                        {formatTime(change.after?.startTime)} - {formatTime(change.after?.endTime)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {actions}
    </div>
  );
};
